import React, { useState, useEffect } from 'react';
import { 
  Card, 
  CardContent, 
  Typography, 
  TextField, 
  Button, 
  Box, 
  Alert, 
  Paper,
  CircularProgress,
  Link
} from '@mui/material';
import { register, isAuthenticated } from '../../services/authService';

/**
 * Register component for user registration
 * @param {Object} props
 * @param {boolean} [props.embedded] - When true, used inside Admin tab (no full-page layout, no redirect when authenticated)
 * @returns {JSX.Element} Register component
 */
const Register = ({ embedded = false } = {}) => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    displayName: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const { username, email, password, confirmPassword, displayName } = formData;
  
  // When not embedded, redirect if user is already logged in
  useEffect(() => {
    if (!embedded && isAuthenticated()) {
      console.log('User is already authenticated, redirecting to home');
      window.location.href = '/';
    }
  }, [embedded]);
  
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setLoading(true);
    
    // Validate inputs
    if (!username || !email || !password || !confirmPassword || !displayName) {
      setError('All fields are required');
      setLoading(false);
      return;
    }
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }
    
    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      setLoading(false);
      return;
    }
    
    console.log('Registration form submitted for user:', username);
    
    try {
      // Register user with role 'user'
      const userData = {
        username,
        email,
        password,
        displayName,
        role: 'engineer' // Ensure all new users are created with 'user' role
      };
      
      await register(userData);
      console.log('Registration successful');
      
      setSuccess(true);
      setLoading(false);
      
      // Clear form
      setFormData({
        username: '',
        email: '',
        password: '',
        confirmPassword: '',
        displayName: ''
      });
      
      // When embedded (admin tab), stay on page; otherwise redirect to login after 2 seconds
      if (!embedded) {
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);
      }
    } catch (err) {
      console.error('Registration error:', err);
      
      // Display user-friendly error message
      if (err.message.includes('already exists')) {
        setError('Username or email already exists. Please try another.');
      } else if (err.message.includes('validation')) {
        setError('Please check your input and try again.');
      } else {
        setError(err.message || 'An error occurred during registration. Please try again.');
      }
      
      setLoading(false);
    }
  };
  
  const wrapperSx = embedded
    ? { maxWidth: 540, width: '100%', animation: 'fadeInUp 0.4s ease-out' }
    : {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        bgcolor: 'background.default',
        background: 'linear-gradient(135deg, #F8F9FA 0%, #E9ECEF 100%)',
        py: 4,
      };
  const innerSx = embedded
    ? { maxWidth: 540, width: '100%', animation: 'fadeInUp 0.4s ease-out' }
    : { maxWidth: 540, width: '100%', px: 2, animation: 'fadeInUp 0.4s ease-out' };

  return (
    <Box sx={wrapperSx}>
      <Box sx={innerSx}>
        <Paper 
          elevation={4}
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
            border: '1px solid rgba(0, 0, 0, 0.05)',
          }}
        >
          <Card sx={{ boxShadow: 'none' }}>
            <CardContent sx={{ p: 4 }}>
              <Box textAlign="center" mb={4}>
                <Typography 
                  variant="h4" 
                  component="h1" 
                  gutterBottom
                  sx={{
                    fontWeight: 600,
                    letterSpacing: '-0.02em',
                    color: 'primary.main',
                  }}
                >
                  Register
                </Typography>
                <Typography 
                  variant="body2" 
                  color="textSecondary"
                  sx={{ mt: 1 }}
                >
                  Create a new account to access HighQ Metrics
                </Typography>
              </Box>
              
              {error && (
                <Alert 
                  severity="error" 
                  sx={{ 
                    mb: 3,
                    borderRadius: 2,
                    animation: 'fadeIn 0.3s ease-out',
                  }}
                >
                  {error}
                </Alert>
              )}
              
              {success && (
                <Alert 
                  severity="success" 
                  sx={{ 
                    mb: 3,
                    borderRadius: 2,
                    animation: 'fadeIn 0.3s ease-out',
                  }}
                >
                  Registration successful! Redirecting to login page...
                </Alert>
              )}
              
              <form onSubmit={handleSubmit}>
                <TextField
                  label="Username"
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  name="username"
                  value={username}
                  onChange={handleChange}
                  required
                  autoFocus
                  disabled={loading || success}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                    mb: 2,
                  }}
                />
                <TextField
                  label="Email"
                  type="email"
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  name="email"
                  value={email}
                  onChange={handleChange}
                  required
                  disabled={loading || success}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                    mb: 2,
                  }}
                />
                <TextField
                  label="Display Name"
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  name="displayName"
                  value={displayName}
                  onChange={handleChange}
                  required
                  disabled={loading || success}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                    mb: 2,
                  }}
                />
                <TextField
                  label="Password"
                  type="password"
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  name="password"
                  value={password}
                  onChange={handleChange}
                  required
                  disabled={loading || success}
                  helperText="Password must be at least 6 characters long"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                    mb: 2,
                  }}
                />
                <TextField
                  label="Confirm Password"
                  type="password"
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  name="confirmPassword"
                  value={confirmPassword}
                  onChange={handleChange}
                  required
                  disabled={loading || success}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                    mb: 3,
                  }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                  size="large"
                  disabled={loading || success}
                  sx={{
                    mt: 1,
                    py: 1.5,
                    borderRadius: 2,
                    fontSize: '1rem',
                    fontWeight: 500,
                    textTransform: 'none',
                    boxShadow: 2,
                    '&:hover': {
                      boxShadow: 4,
                      transform: 'translateY(-2px)',
                    },
                    '&:active': {
                      transform: 'translateY(0)',
                    },
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    'Register'
                  )}
                </Button>
                
                <Box mt={3} textAlign="center">
                  <Typography 
                    variant="body2"
                    sx={{
                      '& a': {
                        fontWeight: 500,
                        transition: 'color 0.2s',
                        '&:hover': {
                          color: 'secondary.main',
                        },
                      },
                    }}
                  >
                    Already have an account?{' '}
                    <Link href="/login" underline="hover">
                      Sign in
                    </Link>
                  </Typography>
                </Box>
              </form>
            </CardContent>
          </Card>
        </Paper>
      </Box>
    </Box>
  );
};

export default Register;
